import type { SeatingFurnitureDTO } from "@/types/Seating";
// Type-only on purpose: `freeSeats.seatAddressOfNumber` delegates HERE, and a
// value import back would make the two modules a cycle.
import type { SeatAddress } from "@/lib/seating/freeSeats";

/**
 * The number PRINTED on a chair, said once.
 *
 * <p>Two numbers name a chair and they are not the same thing:
 *  - `seatIndex` is the stored IDENTITY. Every write, every deep link
 *    (`seat=`), every path parameter and the guest rollup use it, and it is
 *    never renumbered.
 *  - the printed number is what the operator, the place card, the export and
 *    the apps show. Since seat numbering (seats.io slice 1) the SERVER computes
 *    it from the piece's `numbering` and stamps it on the chair as `number`
 *    (and `row` for a theatre block); readers that only see a seat reference
 *    (`SeatEntry`, `guest.seatInfo`, a history row, the hover card) get it as
 *    `seatNumber`/`seatRow`.
 *
 * <p>The display rule everywhere: `seatNumber = number ?? seatIndex + 1`,
 * `seatRow = row ?? null`. A chair without `number` is a legacy chair and reads
 * exactly as it always did. The cockpit never computes a numbering itself —
 * there is no permutation twin here, on purpose.
 */

/**
 * Anything that names a seat: a chair DTO (`number`/`row`) or a seat reference
 * (`seatNumber`/`seatRow`), always with its stored `seatIndex`.
 */
export interface SeatNumberSource {
  seatIndex: number;
  number?: number | null;
  row?: string | null;
  seatNumber?: number | null;
  seatRow?: string | null;
  /** A seat reference's block / area (seats.io Teil 2, Zug 3) — see {@link SeatPlace}. */
  seatBlock?: string | null;
  seatArea?: string | null;
}

/**
 * Where a seat's PIECE stands in its room (seats.io Teil 2, Zug 3): the block
 * it belongs to ("Block C") and that block's area ("Parkett").
 *
 * <p>Not a property of the chair: the server keeps it on the piece as a
 * display cache (`blockName`/`areaName`, stamped from the room on every write
 * of the piece) and copies it onto every seat reference as `seatBlock`/
 * `seatArea` (`SeatEntry`, `guest.seatInfo`, a history row, a guest log, an
 * activity row) — only when the piece has a block; otherwise the keys are
 * ABSENT, never empty. A key here is likewise present only with a non-blank
 * value, so an object without a block compares equal to today's.
 */
export interface SeatPlace {
  seatBlock?: string;
  seatArea?: string;
}

/** A seat reference's own block/area, as the server stamped them (absent / null = none). */
export type SeatPlaceHint = { seatBlock?: string | null; seatArea?: string | null } | null | undefined;

/** A piece's display cache of its block (`SeatingFurnitureDTO.blockName`/`areaName`). */
export type PiecePlaceSource = Pick<SeatingFurnitureDTO, "blockName" | "areaName"> | null | undefined;

/** A seat as it is printed: the number and, on a theatre block, the row label — plus its piece's block/area, when it has one. */
export interface PrintedSeat extends SeatPlace {
  seatNumber: number;
  seatRow: string | null;
}

/** A server-stamped number is a positive integer; anything else is ignored, never shown. */
function stampedNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1 ? value : null;
}

function stampedRow(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** The printed number: `number ?? seatNumber ?? seatIndex + 1`. */
export function seatNumberOf(seat: SeatNumberSource): number {
  return stampedNumber(seat.number) ?? stampedNumber(seat.seatNumber) ?? seat.seatIndex + 1;
}

/** The printed row label, or null when the piece does not label its rows (and on every legacy chair). */
export function seatRowOf(seat: Pick<SeatNumberSource, "row" | "seatRow">): string | null {
  return stampedRow(seat.row) ?? stampedRow(seat.seatRow);
}

export function printedSeatOf(seat: SeatNumberSource): PrintedSeat {
  return { seatNumber: seatNumberOf(seat), seatRow: seatRowOf(seat), ...seatPlaceOf(seat) };
}

/**
 * The separator between the parts of a seat text — "Reihe B · Platz 3",
 * "RT 1 · Platz 4", "Parkett · Block C · …" (U+00B7 with a space each side).
 */
export const SEAT_TEXT_SEPARATOR = " · ";

/** A cached block/area name: a non-blank string, trimmed; anything else is "none". */
function placeName(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function placeFrom(block: unknown, area: unknown): SeatPlace {
  const out: SeatPlace = {};
  const b = placeName(block);
  const a = placeName(area);
  if (b !== null) out.seatBlock = b;
  if (a !== null) out.seatArea = a;
  return out;
}

/** The block/area a seat reference carries (`seatBlock`/`seatArea`); `{}` when it has none. */
export function seatPlaceOf(ref: SeatPlaceHint): SeatPlace {
  return placeFrom(ref?.seatBlock, ref?.seatArea);
}

/** The block/area of a loaded piece (its `blockName`/`areaName` cache); `{}` when it has none. */
export function piecePlaceOf(piece: PiecePlaceSource): SeatPlace {
  return placeFrom(piece?.blockName, piece?.areaName);
}

/**
 * THE display rule for a seat's block and area, said once for every surface
 * (seats.io Teil 2, Zug 3): `[seatArea · ][seatBlock · ]` in front of the text
 * the surface shows today — "Parkett · Block C · Reihe 4 · Platz 12",
 * "Block A · Tisch 5 · Platz 3". The block name stands as it was typed (no
 * word "Block" is added). Without a block the text comes back UNCHANGED, byte
 * for byte — that is the whole contract for every room that has no blocks.
 *
 * <p>Where it goes: in front of the PIECE NAME where the surface names the
 * piece (a block groups pieces, so the place reads before the table), else in
 * front of the seat label. A surface that leaves the piece implicit (the
 * piece's own dialog, its chair list, the seats of the table picked one field
 * above) leaves the block implicit too. An empty `text` gives the place alone
 * ("Parkett · Block C"), never a dangling separator.
 */
export function seatPlaceText(place: SeatPlaceHint, text: string): string {
  const { seatArea, seatBlock } = seatPlaceOf(place);
  const parts: string[] = [];
  if (seatArea !== undefined) parts.push(seatArea);
  if (seatBlock !== undefined) parts.push(seatBlock);
  if (parts.length === 0) return text;
  if (text !== "") parts.push(text);
  return parts.join(SEAT_TEXT_SEPARATOR);
}

/** The translator of the `seating` namespace (`useTranslations("seating")`). */
export type SeatingTranslator = (key: string, values?: Record<string, string | number>) => string;

/**
 * "Seat 4" / "Row B · Seat 3" — `seating.seats.seatNumber` or
 * `seating.seats.seatRowAndNumber`. `t` is the `seating` namespace. Takes a
 * chair, a seat reference or an already resolved {@link PrintedSeat}.
 */
export function seatLabelText(t: SeatingTranslator, seat: SeatNumberSource | PrintedSeat): string {
  const printed = "seatIndex" in seat ? printedSeatOf(seat) : seat;
  return printed.seatRow !== null
    ? t("seats.seatRowAndNumber", { row: printed.seatRow, seatNum: printed.seatNumber })
    : t("seats.seatNumber", { seatNum: printed.seatNumber });
}

/**
 * The chair the operator NAMED by its printed number (and row), or null.
 *
 * <p>Replaces `freeSeats.seatAddressOfNumber`, which read "#4" as `seatIndex 3`
 * — true only while a piece has no numbering. Resolves against the printed
 * number of every chair and hands back both halves of the address (the write
 * needs `position`, the identity is `seatIndex`).
 *
 * <p>Null — never a guess — when no chair prints that number, when the number
 * is not a positive integer, or when it is AMBIGUOUS: a theatre block that
 * restarts per row prints "1" once per row, so without the row there is no
 * single answer. A row is compared case-insensitively ("b" finds row "B"); a
 * row given for a piece without row labels finds nothing.
 */
export function seatAddressOfPrinted(
  furniture: Pick<SeatingFurnitureDTO, "seats"> | null | undefined,
  printedNumber: number,
  row?: string | null,
): SeatAddress | null {
  if (!Number.isInteger(printedNumber) || printedNumber < 1) return null;
  const seats = furniture?.seats;
  if (!Array.isArray(seats)) return null;
  const wantedRow = stampedRow(row)?.toUpperCase() ?? null;
  let hit: SeatAddress | null = null;
  for (let position = 0; position < seats.length; position++) {
    const chair = seats[position];
    if (!chair) continue;
    const seatIndex = chair.seatIndex ?? position;
    if (seatNumberOf({ ...chair, seatIndex }) !== printedNumber) continue;
    if (wantedRow !== null && seatRowOf(chair)?.toUpperCase() !== wantedRow) continue;
    if (hit !== null) return null;
    hit = { position, seatIndex };
  }
  return hit;
}

/** A server-stamped printed seat that rode along with a seat reference (`SeatEntry`, `seatInfo`, a history row). */
export type PrintedSeatHint =
  | { seatNumber?: number | null; seatRow?: string | null; seatBlock?: string | null; seatArea?: string | null }
  | null
  | undefined;

/** What {@link resolveSeat} needs of a loaded piece: its chairs and its block cache. */
export type ResolvablePiece = Pick<SeatingFurnitureDTO, "seats" | "blockName" | "areaName">;

/**
 * How a seat known only by its reference — furniture + stored index — is
 * printed. In order:
 *  1. the loaded chair (`getFurniture`, e.g. the seating store's): the live
 *     answer, already showing a numbering saved a second ago — and the
 *     piece's CURRENT block/area (its `blockName`/`areaName` cache), so a
 *     block renamed a second ago reads the new name;
 *  2. the server's stamp that came with the reference (`hint`), block/area
 *     included;
 *  3. `seatIndex + 1` — the legacy rule, and the answer of an older backend
 *     (with the hint's block/area, should one ride along without a number).
 */
export function resolveSeat(
  getFurniture: (furnitureId: string) => ResolvablePiece | null | undefined,
  furnitureId: string | null | undefined,
  seatIndex: number,
  hint?: PrintedSeatHint,
): PrintedSeat {
  const furniture = furnitureId ? getFurniture(furnitureId) : null;
  // By the STORED index, never by position — the same rule as
  // `freeSeats.seatAddressOfStored`: a miss means the chair is gone, and
  // guessing would print a different chair's number.
  const chair = Array.isArray(furniture?.seats)
    ? furniture.seats.find((s) => s && s.seatIndex === seatIndex)
    : undefined;
  if (chair) {
    const { seatNumber, seatRow } = printedSeatOf({ ...chair, seatIndex });
    return { seatNumber, seatRow, ...piecePlaceOf(furniture) };
  }
  const hinted = stampedNumber(hint?.seatNumber);
  if (hinted !== null) return { seatNumber: hinted, seatRow: stampedRow(hint?.seatRow), ...seatPlaceOf(hint) };
  return { seatNumber: seatIndex + 1, seatRow: null, ...seatPlaceOf(hint) };
}

/** {@link resolveSeat}, number only. */
export function resolveSeatNumber(
  getFurniture: (furnitureId: string) => ResolvablePiece | null | undefined,
  furnitureId: string | null | undefined,
  seatIndex: number,
  hint?: PrintedSeatHint,
): number {
  return resolveSeat(getFurniture, furnitureId, seatIndex, hint).seatNumber;
}

/**
 * "RT 1 · Seat 4" / "RT 1 · Row B · Seat 3" — `seating.seats.seatedAt` or
 * `seating.seats.seatedAtRow`, the line a seat-popup / furniture-dialog
 * candidate shows under a guest who already sits somewhere. `t` is the
 * `seating` namespace.
 *
 * <p>It names the piece, so a seat that carries a block/area (a `SeatEntry`
 * stamped by the server, a {@link PrintedSeat} that resolved one) reads it in
 * front of the piece: "Parkett · Block C · RT 1 · Seat 4" ({@link seatPlaceText}).
 */
export function seatedAtText(t: SeatingTranslator, table: string, seat: SeatNumberSource | PrintedSeat): string {
  const printed = "seatIndex" in seat ? printedSeatOf(seat) : seat;
  const placed = seatPlaceText(printed, table);
  return printed.seatRow !== null
    ? t("seats.seatedAtRow", { table: placed, row: printed.seatRow, seatNum: printed.seatNumber })
    : t("seats.seatedAt", { table: placed, seatNum: printed.seatNumber });
}

/**
 * The bare printed address where a column has no room for a word: "4", or
 * "B · 3" on a theatre block (the same short form the seating PDF prints in
 * its seat columns).
 */
export function seatShortText(seat: SeatNumberSource | PrintedSeat): string {
  const printed = "seatIndex" in seat ? printedSeatOf(seat) : seat;
  return printed.seatRow !== null ? `${printed.seatRow} · ${printed.seatNumber}` : String(printed.seatNumber);
}

/**
 * Row labels in the order a theatre counts them: shorter first, then
 * alphabetically — "A" … "Z", "AA" and "1" … "9", "10" both come out right,
 * where a plain string compare puts "AA" before "B" and "10" before "2".
 */
function compareRowLabels(a: string, b: string): number {
  if (a.length !== b.length) return a.length - b.length;
  const ua = a.toUpperCase();
  const ub = b.toUpperCase();
  return ua < ub ? -1 : ua > ub ? 1 : 0;
}

/**
 * The order a list SHOWN to the operator puts chairs in: by the printed
 * address — row (a chair without a row first), then the printed number — and
 * the stored index as the last tie-break (a theatre block restarting per row
 * prints "1" once per row; drifted legacy data can repeat a number). On a
 * legacy piece this is exactly the old `seatIndex` order.
 *
 * Display only. Logic that PICKS a chair ("the first free one", the party
 * neighbours) keeps the physical index order the server's planner uses.
 */
export function comparePrintedSeats(a: SeatNumberSource, b: SeatNumberSource): number {
  const ra = seatRowOf(a);
  const rb = seatRowOf(b);
  if (ra !== rb) {
    if (ra === null) return -1;
    if (rb === null) return 1;
    const byRow = compareRowLabels(ra, rb);
    if (byRow !== 0) return byRow;
  }
  return seatNumberOf(a) - seatNumberOf(b) || a.seatIndex - b.seatIndex;
}
